import React, { FC, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Alert, Button, Drawer, Empty, Input, Spin, Table, Tag } from 'antd';
import {
	CheckCircleOutlined,
	CloseCircleOutlined,
	FileTextOutlined,
	FilterOutlined,
	LinkOutlined,
	SyncOutlined,
} from '@ant-design/icons';
import { complianceDocumentsRestCrudThunks } from '../../thunks/compliance_documents_thunks';
import {
	describeUnevaluatableFile,
	evaluateComplianceDocumentAgainstChecklist,
	fetchComplianceDocumentAsFile,
} from '../../thunks/compliance_checklists_thunks';
import {
	linkComplianceDocumentToInstance,
	replaceComplianceDocumentOnInstance,
} from '../../thunks/compliance_request_instances_thunks';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	COMPLIANCE_EXPIRY_STATUSES,
	ComplianceDocument,
	ComplianceRequestInstance,
} from '../../types/ComplianceTypes';
import {
	ChecklistEvaluation,
	EVALUATION_OVERALL_STATUSES,
} from '../../types/ComplianceChecklistTypes';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { deriveComplianceExpiryStatus } from '../../utils/ComplianceUtils';
import { deriveOverallEvaluationStatus } from '../../utils/ComplianceEvaluationUtils';
import { ComplianceAiLoadingState } from '../compliance_common/ComplianceAiLoadingState';
import { ComplianceEvaluationResults } from '../compliance_common/ComplianceEvaluationResults';
import {
	COMPLIANCE_EXPIRY_STATUS_COLORS,
	COMPLIANCE_EXPIRY_STATUS_DISPLAY_NAMES,
} from '../compliance_common/ComplianceConstants';
import OWButton from '../ow_button/OWButton';
import AddComplianceDocumentModal from './AddComplianceDocumentModal';
import useMountEffect from '../../hooks/useMountEffect';

const CANDIDATES_TARGET_COLLECTION = 'linkDocumentCandidates';
const PAGE_SIZE = 10;

// The drawer is a two-act flow when the client's request carries a checklist:
// pick a document, then read what the AI found before committing the link.
const PHASES = { PICKING: 'picking', EVALUATING: 'evaluating', RESULTS: 'results' };

const formatDate = (isoDate) => (isoDate ? moment(isoDate).format('D MMM, YYYY') : '--');

// Icon per derived expiry status (documents have no server expiryStatus).
const EXPIRY_ICONS = {
	[COMPLIANCE_EXPIRY_STATUSES.UP_TO_DATE]: CheckCircleOutlined,
	[COMPLIANCE_EXPIRY_STATUSES.EXPIRING_SOON]: SyncOutlined,
	[COMPLIANCE_EXPIRY_STATUSES.EXPIRED]: CloseCircleOutlined,
};

interface ChooseComplianceDocumentModalProps {
	visible: boolean;
	// 'link' (default) links a document to an instance with no doc yet; 'replace'
	// swaps the currently-linked document for a different one.
	mode?: 'link' | 'replace';
	instance: ComplianceRequestInstance;
	onCancel: () => void;
	onSuccess: () => void;
	candidateDocuments: ComplianceDocument[];
	total: number;
	fetching: boolean;
	fetchErrors: string[];
	linking: boolean;
	fetchCandidates: (params: any, pagination: { current: number; pageSize: number }) => Promise<any>;
	linkDocument: (
		instanceId: number,
		complianceDocumentId: number,
		checklistEvaluation: ChecklistEvaluation
	) => Promise<any>;
	replaceDocument: (
		instanceId: number,
		complianceDocumentId: number,
		checklistEvaluation: ChecklistEvaluation
	) => Promise<any>;
	evaluateDocument: (instanceId: number, documentFile: File) => Promise<ChecklistEvaluation>;
}

const ChooseComplianceDocumentModal: FC<ChooseComplianceDocumentModalProps> = ({
	visible,
	mode = 'link',
	instance,
	onCancel,
	onSuccess,
	candidateDocuments,
	total,
	fetching,
	fetchErrors,
	linking,
	fetchCandidates,
	linkDocument,
	replaceDocument,
	evaluateDocument,
}): React.ReactElement => {
	const isReplaceMode = mode === 'replace';
	const [selectedDocumentId, setSelectedDocumentId] = useState(null);
	const [addDocumentModalOpen, setAddDocumentModalOpen] = useState(false);
	// When true, the request's compliance-type filter is cleared (all docs shown).
	const [typeFilterCleared, setTypeFilterCleared] = useState(false);
	const [searchText, setSearchText] = useState('');
	const [candidatePage, setCandidatePage] = useState(1);

	const [phase, setPhase] = useState<string>(PHASES.PICKING);
	// The document the supplier is committing to, and the file it was checked
	// from. Held here rather than derived from selectedDocumentId because a
	// freshly uploaded document is not in the candidate list yet.
	const [pendingDocument, setPendingDocument] = useState<ComplianceDocument>(null);
	const [evaluation, setEvaluation] = useState<ChecklistEvaluation>(null);
	// Why the AI check did not happen or did not finish. The check is help, not a
	// gate, so this is shown alongside a link button rather than blocking.
	const [evaluationSkipReason, setEvaluationSkipReason] = useState<string>(null);

	const request = nullSafeGetOrElse('complianceRequest', instance, {});
	const typeName = nullSafeGet('complianceType.name', request);
	const requestTypeId = nullSafeGet('complianceTypeId', request);
	// The client's requirements, read from their sample when they created the
	// request. No checklist means nothing to check against, so the drawer stays
	// the plain picker it has always been.
	const checklistItems = nullSafeGetOrElse('checklistExtraction.items', request, []);
	const hasChecklist = checklistItems.length > 0;

	// Server filter params derived from the current UI state. Undefined-valued keys
	// are omitted so we never send `search: undefined` / `complianceTypeId: undefined`.
	//
	// BACKEND FOLLOW-UP: this candidate list should also be scoped to the requesting
	// client (instance.buyerCompanyId) — a supplier shouldn't be offered documents
	// shared only with other clients. The /compliance/documents endpoint has no
	// buyerCompanyId filter param today, and filtering client-side would break the
	// server pagination, so add a buyerCompanyId (shared-with) filter to that endpoint
	// and pass it here. (The backend still rejects an invalid cross-client link at
	// submit, so this is a UX/hardening gap, not a data-exposure hole at write time.)
	const buildParams = () => {
		const params: any = {};
		if (request.countryCode) {
			params.countryCode = request.countryCode;
		}
		const trimmed = searchText.trim();
		if (trimmed) {
			params.search = trimmed;
		}
		if (!!requestTypeId && !typeFilterCleared) {
			params.complianceTypeId = requestTypeId;
		}
		return params;
	};

	// Clear any selection on a candidate reload — the previously-selected doc may not
	// be in the new result set, and a hidden selection must never be linked.
	const loadPage = (page) => {
		setSelectedDocumentId(null);
		return fetchCandidates(buildParams(), { current: page, pageSize: PAGE_SIZE });
	};

	useMountEffect(() => {
		loadPage(1);
	});

	// Debounce search so it fires once the user pauses, not per keystroke. The
	// `=== undefined ? null` guard skips the initial mount so it doesn't double-fetch
	// alongside the mount effect (mirrors AdvancedFilters).
	const searchTimer = useRef(undefined);
	useEffect(() => {
		clearTimeout(searchTimer.current);
		searchTimer.current =
			searchTimer.current === undefined
				? null
				: setTimeout(() => {
						setCandidatePage(1);
						loadPage(1);
				  }, 400);
	}, [searchText]);

	const openAddDocumentModal = () => setAddDocumentModalOpen(true);
	const closeAddDocumentModal = () => setAddDocumentModalOpen(false);

	// Commits the link. `checklistEvaluation` rides along so the verdict the
	// supplier just read is what their client's reviewer sees; the backend stores
	// it verbatim and a null overwrites whatever was there before.
	const performLink = (
		complianceDocument: ComplianceDocument,
		checklistEvaluation: ChecklistEvaluation
	) => {
		const action = isReplaceMode ? replaceDocument : linkDocument;
		return action(instance.id, complianceDocument.id, checklistEvaluation).then(onSuccess);
	};

	// Checks a document against the client's checklist before linking it. The file
	// is passed in for a fresh upload (we already hold the bytes) and re-read from
	// the backend otherwise, since the evaluate endpoint takes bytes, not an id.
	// Every failure path lands on the results step with a reason rather than
	// stranding the supplier — they can always link without the check.
	const startEvaluation = (complianceDocument: ComplianceDocument, uploadedFile: File) => {
		setPendingDocument(complianceDocument);
		setEvaluation(null);
		setEvaluationSkipReason(null);
		if (!hasChecklist) {
			performLink(complianceDocument, null);
			return;
		}
		setPhase(PHASES.EVALUATING);
		const resolveFile = uploadedFile
			? Promise.resolve(uploadedFile)
			: fetchComplianceDocumentAsFile(complianceDocument);
		resolveFile
			.then((documentFile) => {
				const skipReason = describeUnevaluatableFile(documentFile);
				if (skipReason) {
					return Promise.reject(skipReason);
				}
				return evaluateDocument(instance.id, documentFile);
			})
			.then((checklistEvaluation) => {
				setEvaluation(checklistEvaluation);
				setPhase(PHASES.RESULTS);
			})
			.catch((error) => {
				setEvaluationSkipReason(
					typeof error === 'string'
						? error
						: nullSafeGetOrElse(
								'message',
								error,
								"We could not check this document against your client's requirements."
						  )
				);
				setPhase(PHASES.RESULTS);
			});
	};

	const handleAddDocumentSuccess = (savedDocument: ComplianceDocument, uploadedFile: File) => {
		setAddDocumentModalOpen(false);
		setCandidatePage(1);
		loadPage(1);
		// A document added from inside this drawer is being added in order to be
		// linked, so carry straight on into the check instead of dropping the
		// supplier back onto the picker to find what they just uploaded.
		if (nullSafeGet('id', savedDocument)) {
			startEvaluation(savedDocument, uploadedFile);
		}
	};

	const handleConfirm = () => {
		const selectedDocument = candidateDocuments.filter((doc) => doc.id === selectedDocumentId)[0];
		if (!selectedDocument) {
			return;
		}
		startEvaluation(selectedDocument, null);
	};

	// Back to the picker: the evaluation belongs to the document that was checked,
	// so none of it survives choosing a different one.
	const handleBackToPicking = () => {
		setPhase(PHASES.PICKING);
		setPendingDocument(null);
		setEvaluation(null);
		setEvaluationSkipReason(null);
	};

	const handleLinkAfterEvaluation = () => performLink(pendingDocument, evaluation);

	// The type filter narrows the server query to the request's compliance type.
	// When active, the server returns strictly that type (an intentional change from
	// the old client filter, which also surfaced untyped docs).
	const typeFilterActive = !!requestTypeId && !typeFilterCleared;

	// Toggling the type filter refetches page 1 with params computed from the intended
	// next state — state updates are async, so we can't read `typeFilterCleared` yet.
	const applyTypeFilterCleared = (next) => {
		setTypeFilterCleared(next);
		setCandidatePage(1);
		setSelectedDocumentId(null);
		const params: any = {};
		if (request.countryCode) {
			params.countryCode = request.countryCode;
		}
		const trimmed = searchText.trim();
		if (trimmed) {
			params.search = trimmed;
		}
		if (!!requestTypeId && !next) {
			params.complianceTypeId = requestTypeId;
		}
		fetchCandidates(params, { current: 1, pageSize: PAGE_SIZE });
	};

	// Derived expiry status + date(s) — mirrors the Documents tab. Documents carry
	// effectiveDate/expiryDate but no server expiryStatus, so it's derived client-side.
	const renderExpiration = (doc: ComplianceDocument) => {
		const effectiveDate = nullSafeGet('effectiveDate', doc);
		const expiryDate = nullSafeGet('expiryDate', doc);
		if (!effectiveDate && !expiryDate) {
			return <span className="text-character-secondary">--</span>;
		}
		const status = deriveComplianceExpiryStatus(expiryDate);
		const color = status ? COMPLIANCE_EXPIRY_STATUS_COLORS[status] : undefined;
		const IconComponent = status ? EXPIRY_ICONS[status] : null;
		return (
			<div className="flex flex-col gap-1">
				{status ? (
					<span
						className="footnoteregular inline-flex flex-row items-center gap-1"
						style={{ color }}
					>
						{IconComponent ? <IconComponent /> : null}
						{COMPLIANCE_EXPIRY_STATUS_DISPLAY_NAMES[status]}
					</span>
				) : null}
				{effectiveDate && expiryDate ? (
					<>
						<span className="footnoteregular text-character-secondary">
							Effective {formatDate(effectiveDate)}
						</span>
						<span className="footnoteregular text-character-secondary">
							Expiry: {formatDate(expiryDate)}
						</span>
					</>
				) : expiryDate ? (
					<span className="footnoteregular text-character-secondary">{formatDate(expiryDate)}</span>
				) : (
					<span className="footnoteregular text-character-secondary">
						Effective {formatDate(effectiveDate)}
					</span>
				)}
			</div>
		);
	};

	const renderName = (doc: ComplianceDocument) => {
		const docTypeName = nullSafeGet('complianceType.name', doc);
		return (
			<div className="flex flex-col gap-1">
				<span className="bodymedium">{doc.name}</span>
				{docTypeName ? (
					<span className="footnoteregular text-character-secondary">{docTypeName}</span>
				) : null}
			</div>
		);
	};

	const renderFileLink = (doc: ComplianceDocument) => {
		if (!doc.fileUrl) {
			return <span className="text-character-secondary">--</span>;
		}
		const fileLabel = nullSafeGetOrElse('fileName', doc, 'document');
		return (
			<a
				className="flex min-w-0 flex-row items-center gap-1"
				href={doc.fileUrl}
				target="_blank"
				rel="noopener noreferrer"
				title={fileLabel}
			>
				<FileTextOutlined className="shrink-0" />
				<span className="truncate">{fileLabel}</span>
			</a>
		);
	};

	// A columnar Table (like the Documents tab) rather than a CSS grid — antd sizes
	// columns + row height reliably, avoiding the flex/grid collapse the grid hit.
	const documentColumns = [
		{ title: 'Compliance Doc', key: 'name', width: '38%', render: (text, doc) => renderName(doc) },
		{
			title: 'Expiration Date',
			key: 'expiration',
			width: '32%',
			render: (text, doc) => renderExpiration(doc),
		},
		{ title: 'File', key: 'file', width: '30%', render: (text, doc) => renderFileLink(doc) },
	];

	const rowSelection = {
		type: 'radio' as const,
		selectedRowKeys: selectedDocumentId ? [selectedDocumentId] : [],
		onChange: (selectedRowKeys) => setSelectedDocumentId(selectedRowKeys[0]),
	};

	const renderBody = () => {
		if (fetching && candidateDocuments.length === 0) {
			return (
				<div className="flex justify-center py-12">
					<Spin />
				</div>
			);
		}
		if (fetchErrors.length > 0) {
			return (
				<Alert
					type="error"
					message="Couldn't load your compliance documents."
					description={fetchErrors.join(', ')}
				/>
			);
		}
		const emptyText = (
			<Empty
				image={Empty.PRESENTED_IMAGE_SIMPLE}
				description={`No matching compliance documents. Add one to ${
					isReplaceMode ? 'replace the linked document' : 'link it to this request'
				}.`}
			/>
		);
		return (
			<Table
				columns={documentColumns}
				dataSource={candidateDocuments}
				rowKey={(doc) => doc.id}
				rowSelection={rowSelection}
				pagination={{
					current: candidatePage,
					pageSize: PAGE_SIZE,
					total,
					showSizeChanger: false,
					onChange: (page) => {
						setCandidatePage(page);
						loadPage(page);
					},
				}}
				showHeader={true}
				locale={{ emptyText }}
				rowClassName="clickable-paginated-index-row"
				onRow={(doc) => ({ onClick: () => setSelectedDocumentId(doc.id) })}
			/>
		);
	};

	// The AI check, or an explanation of why it did not happen. Either way the
	// supplier still has a link button — a failed check never traps a document.
	const renderEvaluation = () => {
		if (evaluation) {
			return (
				<ComplianceEvaluationResults
					evaluation={evaluation}
					checklistItems={checklistItems}
					fileName={nullSafeGet('fileName', pendingDocument)}
				/>
			);
		}
		return (
			<Alert
				type="warning"
				showIcon={true}
				message="We could not check this document automatically"
				description={`${evaluationSkipReason} You can still link it — your client will review it as usual.`}
			/>
		);
	};

	const commitLabel = isReplaceMode ? 'Replace Document' : 'Link Document';
	const meetsRequirements =
		!!evaluation &&
		deriveOverallEvaluationStatus(evaluation, checklistItems) ===
			EVALUATION_OVERALL_STATUSES.COMPLIANT;

	const renderFooter = () => {
		if (phase === PHASES.EVALUATING) {
			return (
				<div className="flex flex-row justify-end gap-2">
					<Button onClick={onCancel}>Cancel</Button>
				</div>
			);
		}
		if (phase === PHASES.RESULTS) {
			return (
				<div className="flex flex-row justify-end gap-2">
					<Button onClick={handleBackToPicking}>Back</Button>
					<OWButton
						type="primary"
						icon={<LinkOutlined />}
						danger={!meetsRequirements}
						loading={linking}
						onClick={handleLinkAfterEvaluation}
					>
						{meetsRequirements ? commitLabel : 'Link Anyway'}
					</OWButton>
				</div>
			);
		}
		return (
			<div className="flex flex-row justify-end gap-2">
				<Button onClick={onCancel}>Cancel</Button>
				<OWButton
					type="primary"
					icon={<LinkOutlined />}
					disabled={
						!selectedDocumentId || !candidateDocuments.some((doc) => doc.id === selectedDocumentId)
					}
					loading={linking}
					onClick={handleConfirm}
				>
					{hasChecklist ? 'Check Document' : commitLabel}
				</OWButton>
			</div>
		);
	};

	const drawerTitle =
		phase === PHASES.PICKING
			? isReplaceMode
				? 'Choose a Compliance Document to replace'
				: 'Choose a Compliance Document to link'
			: `Checking against ${nullSafeGetOrElse(
					'buyerCompany.name',
					instance,
					'your client'
			  )}'s requirements`;

	return (
		<Drawer
			visible={visible}
			title={drawerTitle}
			onClose={onCancel}
			placement="right"
			width="50%"
			destroyOnClose={true}
			footer={renderFooter()}
		>
			{phase === PHASES.EVALUATING ? (
				<ComplianceAiLoadingState
					title="Checking your document"
					description="We are reading your document against this client's requirements so you can fix anything before they see it. This usually takes under a minute."
					fileName={nullSafeGet('fileName', pendingDocument)}
				/>
			) : null}
			{phase === PHASES.RESULTS ? renderEvaluation() : null}
			<div className={phase === PHASES.PICKING ? '' : 'hidden'}>
				<div className="mb-3 flex flex-row items-center gap-2">
					<Input.Search
						placeholder="Search by document name"
						value={searchText}
						onChange={(event) => setSearchText(event.target.value)}
						style={{ maxWidth: 280 }}
						allowClear={true}
					/>
					{requestTypeId ? (
						<Button
							type={typeFilterActive ? 'primary' : 'default'}
							icon={<FilterOutlined />}
							onClick={() => applyTypeFilterCleared(!typeFilterCleared)}
						/>
					) : null}
					<Button className="ml-auto" type="primary" ghost={true} onClick={openAddDocumentModal}>
						+ Add Document
					</Button>
				</div>
				{typeFilterActive && typeName ? (
					<div className="mb-3">
						<Tag closable={true} onClose={() => applyTypeFilterCleared(true)}>
							Compliance Type: {typeName}
						</Tag>
					</div>
				) : null}
				{renderBody()}
			</div>
			{addDocumentModalOpen && (
				<AddComplianceDocumentModal
					visible={addDocumentModalOpen}
					requiredBuyerCompanyId={nullSafeGet('buyerCompanyId', instance)}
					onCancel={closeAddDocumentModal}
					onSuccess={handleAddDocumentSuccess}
				/>
			)}
		</Drawer>
	);
};

const mapStateToProps = (state) => ({
	candidateDocuments: getRecordsForTargetCollection(
		state.compliance_documents,
		CANDIDATES_TARGET_COLLECTION
	),
	total: nullSafeGetOrElse(
		`${CANDIDATES_TARGET_COLLECTION}.pagination.total`,
		state.compliance_documents,
		0
	),
	fetching: state.compliance_documents.fetching,
	fetchErrors: state.compliance_documents.fetchErrors,
	linking: state.compliance_request_instances.updating,
});

const mapDispatchToProps = (dispatch) => ({
	fetchCandidates: (params, pagination) =>
		dispatch(
			complianceDocumentsRestCrudThunks(ROLE_TYPES.SUPPLIER).read(
				params,
				CANDIDATES_TARGET_COLLECTION,
				pagination
			)
		),
	// checklistEvaluation is a full replace on the backend: sending undefined
	// nulls whatever was stored, which is what we want when the check was skipped.
	linkDocument: (instanceId, complianceDocumentId, checklistEvaluation) =>
		dispatch(
			linkComplianceDocumentToInstance(ROLE_TYPES.SUPPLIER)(instanceId, {
				complianceDocumentId,
				checklistEvaluation,
			})
		),
	replaceDocument: (instanceId, complianceDocumentId, checklistEvaluation) =>
		dispatch(
			replaceComplianceDocumentOnInstance(ROLE_TYPES.SUPPLIER)(instanceId, {
				complianceDocumentId,
				checklistEvaluation,
			})
		),
	evaluateDocument: (instanceId, documentFile) =>
		dispatch(
			evaluateComplianceDocumentAgainstChecklist(ROLE_TYPES.SUPPLIER)(instanceId, documentFile)
		),
});

export default connect(mapStateToProps, mapDispatchToProps)(ChooseComplianceDocumentModal);
